import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Booking } from '../../models/Booking'
import { Charge } from '../../models/Charge'
import { Estimate } from '../../models/Estimate'
import { Gift } from '../../models/Gift'
import { Invoice } from '../../models/Invoice'
import { Order } from '../../models/Order'
import { Package } from '../../models/Package'
import { Payment } from '../../models/Payment'
import { Reader } from '../../models/Reader'
import { RecordFactory } from '../../models/RecordFactory'
import { Subaccount } from '../../models/Subaccount'
import { Subscription } from '../../models/Subscription'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import {
    useNotificationListener,
    useNotificationsManagerContext,
} from '../../providers/NotificationsManagerProvider'
import { usePopoverManagerContext } from '../../providers/PopoverManagerProvider'
import { Result, errorResult, okResult } from '../../types/utils'
import { capitalizeFirstLetters } from '../../utils'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { NewChargePage, POS_LIMIT } from './NewChargePage'
import { NewChargeGratuityPage } from './NewChargeGratuityPage'
import { NewChargePaymentPage } from './NewChargePaymentPage'
import { ProcessingFailedPopover, NewChargeProcessingPopover } from './NewChargeProcessingPopover'
import { NewChargeCompletePage } from './NewChargeCompletePage'
import { initializePayment } from './initPaymentHelpers'
import {
    calculatePackageSurcharge,
    sendChargeToReader,
    sendMarkPaid,
    submitCharge,
} from './sendPaymentHelpers'
import { useChargeFlowStepManager } from './useChargeFlowStepManager'

export type AllowedTrandocs = Booking | Estimate | Invoice | Gift | Package | Order | Subscription

export type AllowedTrandocTypes =
    | 'booking'
    | 'estimate'
    | 'invoice'
    | 'gift'
    | 'package'
    | 'order'
    | 'subscription'

const getOriginalRecord = (payment: Payment | Charge | AllowedTrandocs) => {
    if (payment instanceof Charge) {
        return undefined
    }

    if (payment instanceof Payment) {
        switch (payment.type) {
            case 'invoice':
                return payment.invoice
            case 'booking':
                return payment.booking
            case 'estimate':
                return payment.estimate
            case 'gift':
                return payment.gift
            case 'package':
                return payment.aPackage
            case 'order':
                return payment.order
            case 'subscription':
                return payment.subscription
            default:
                return undefined
        }
    }

    return payment
}

const viewWillAppear = async (base: PocketComponent<{ action: 'new' | 'copy' | 'charge' }>) => {
    const { recordType, recordID, subType } = base.urlParams as {
        recordType?: AllowedTrandocTypes
        recordID?: string
        subType?: string
    }

    let client: User | undefined
    const clientId = base.searchParams.get('clientId')
    if (clientId) {
        client = await RecordFactory.getInstance().loadUser(clientId)
    }

    let subaccount: Subaccount | undefined
    const subaccountId = base.searchParams.get('subaccountId')
    if (subaccountId) {
        subaccount = await RecordFactory.getInstance().loadSubaccount(subaccountId)
    }

    const initialization = await initializePayment(
        base.me,
        base.owner,
        recordType,
        recordID,
        subType,
        client,
        subaccount,
        base.props.action === 'copy' ? base.urlParams.id : undefined
    )

    const { payment, ...rest } = initialization

    const [items, categories, readers] = await Promise.all([
        RecordFactory.getInstance().loadItems(base.owner.id!),
        RecordFactory.getInstance().loadCategories(base.owner.id!),
        RecordFactory.getInstance().loadReaders(),
    ])

    return {
        payment,
        ...rest,
        recordType: recordType ?? 'charge',
        items,
        categories,
        readers: readers.filter((r) => r.type === 'bbpos_wisee'), // all others are incompatible with desktop
    }
}

const ChargeWorkflowRoot = ({
    data,
    base: { logAndDisplayError, me },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    usePageTitle('Charge')

    const {
        payment: originalPayment,
        trandoc,
        taxable,
        canEditTotal,
        canProceedWhenTotalIsZero,
        minimumAmount,
        maximumAmount,
        items,
        categories,
        readers,
        viewRecordPathname,
        usesPaymentPlan,
        recordTypeName,
        canPayViaOther,
    } = data

    // maximum amount + any additional costs like surcharges  & taxes
    const trueMaximumAmount = useMemo(() => {
        let max = maximumAmount ?? POS_LIMIT
        if (usesPaymentPlan && originalPayment.type === 'package' && originalPayment.aPackage) {
            max += calculatePackageSurcharge(originalPayment.aPackage)
            max += originalPayment.aPackage?.taxAmount ?? 0
            return max
        }

        if (usesPaymentPlan && originalPayment.type === 'invoice') {
            max += originalPayment.invoice?.surchargeAmount ?? 0
            max += originalPayment.invoice?.taxAmount ?? 0
            return max
        }

        if (me.allowSurcharge) {
            max += originalPayment.surchargeAmount ?? 0
        }
        if (me.allowSalesTax) {
            max += originalPayment.taxAmount ?? 0
        }
        return max
    }, [
        me.allowSurcharge,
        maximumAmount,
        originalPayment.surchargeAmount,
        originalPayment.taxAmount,
    ])

    const [payment, setPayment] = useState(originalPayment)

    const [finalCharge, setFinalCharge] = useState<AllowedTrandocs | Charge | Payment | undefined>(
        undefined
    )
    const [selectedReader, setSelectedReader] = useState<Reader | undefined>(undefined)
    const { postNotification } = useNotificationsManagerContext()
    const { hidePopover, showPopover } = usePopoverManagerContext()
    const [intent, setIntent] = useState<string | undefined>(undefined)

    const navigate = useNavigate()
    const [_, setSearchParams] = useSearchParams()

    const { steps, currentStep, goToNextStep, goToPreviousStep, backButtonTitle } =
        useChargeFlowStepManager(canEditTotal, me.allowGratuity, recordTypeName)

    const hasExceededMax = useMemo(() => {
        return (payment.total ?? 0) > trueMaximumAmount
    }, [payment.chargeAmount, trueMaximumAmount])

    const isLessThanMin = useMemo(() => {
        return (payment.chargeAmount ?? 0) < (minimumAmount ?? 0)
    }, [payment.chargeAmount, minimumAmount])

    const willFulfillPaymentPlan = useMemo(() => {
        if (!usesPaymentPlan || !payment.total) return false
        if (payment.type === 'invoice') {
            return (payment.total ?? 0) >= (payment.invoice?.owedAmount ?? 0)
        }
        if (payment.type === 'package') {
            return (payment.total ?? 0) >= (payment.aPackage?.owedAmount ?? 0)
        }
        return false
    }, [payment.total, payment.owedAmount, usesPaymentPlan])

    useEffect(() => {
        // this is to ensure that a refresh or something doesn't take us to the send step without a final charge
        if (currentStep.type === 'send' && !finalCharge) {
            setSearchParams({ step: 'payment' })
        }
        if (currentStep.type === 'gratuity') {
            if (!payment.source && !selectedReader && !payment.paymeth && !payment.markPaidDate) {
                setSearchParams({ step: 'payment' })
            }
        }
    }, [payment.client, currentStep.index, setSearchParams])

    // toggles the surcharge checkbox if the payment is going to be marked as paid
    useEffect(() => {
        const willBeMarkedPaid =
            Boolean(payment.paymeth) &&
            Boolean(payment.markPaidDate) &&
            !payment.source &&
            !selectedReader

        if (willBeMarkedPaid) {
            setPayment(payment.clone({ allow_surcharge: false }))
        }
        else {
            setPayment(payment.clone({ allow_surcharge: originalPayment.allowSurcharge }))
        }
    }, [payment.paymeth, payment.markPaidDate, payment.source, selectedReader, originalPayment])

    const isNextEnabled = useMemo(() => {
        if (currentStep.type === 'charge') {
            return (
                ((payment.total !== undefined && payment.lines.length > 0) ||
                    canProceedWhenTotalIsZero) &&
                !hasExceededMax &&
                !isLessThanMin
            )
        }
        else if (currentStep.type === 'payment') {
            return (
                Boolean(selectedReader) ||
                Boolean(payment.source) ||
                (Boolean(payment.paymeth) && Boolean(payment.markPaidDate))
            )
        }
        else if (currentStep.type === 'gratuity') {
            return true
        }
    }, [payment, currentStep, hasExceededMax, isLessThanMin, canProceedWhenTotalIsZero])

    const handlePayment = useCallback(async (): Promise<
        Result<AllowedTrandocs | Charge | Payment | undefined>
    > => {
        // handle reader payments
        if (payment.pos && selectedReader) {
            showPopover(
                <NewChargeProcessingPopover
                    title="Processing reader payment"
                    subtitle="Payment processing on card reader. Do not refresh or exit this page."
                    onCancel={async () => {
                        hidePopover()
                        await RecordFactory.getInstance().cancelInternetReaderIntent(selectedReader)
                        postNotification('IntentStatusChanged', {
                            pendingIntent: undefined,
                            status: 'canceled',
                        })
                    }}
                />
            )

            const result = await sendChargeToReader(payment, selectedReader)
            if (!result.ok) {
                return errorResult(result.error)
            }

            postNotification('IntentStatusChanged', {
                pendingIntent: result.value.intent,
                status: 'pending',
            })

            setIntent(result.value.intent)

            return okResult(undefined)
        }
        // handle card payments
        else if (payment.source) {
            showPopover(
                <NewChargeProcessingPopover
                    title="Processing card payment"
                    subtitle="Payment processing. Do not refresh or exit this page."
                />
            )
            return await submitCharge(payment, trandoc)
        }
        // handle other payments, aka mark as paid
        else if (payment.paymeth && payment.markPaidDate) {
            showPopover(
                <NewChargeProcessingPopover
                    title="Processing"
                    subtitle="Payment processing. Do not refresh or exit this page."
                />
            )
            return await sendMarkPaid(payment, trandoc)
        }

        return { ok: true, value: payment }
    }, [
        payment,
        selectedReader,
        usesPaymentPlan,
        logAndDisplayError,
        postNotification,
        hidePopover,
        showPopover,
    ])

    const handleChargeAndNavigate = useCallback(
        async (offset: number = 1) => {
            const result = await handlePayment()
            if (!result.ok) {
                return showPopover(
                    <ProcessingFailedPopover
                        message={result.error?.toString() ?? 'Please try another payment method'}
                        onClose={() => {
                            hidePopover()
                        }}
                    />
                )
            }

            if (result.value === undefined && selectedReader) {
                // waiting for intent from websocket
                return showPopover(
                    <NewChargeProcessingPopover
                        title="Waiting for response"
                        subtitle="Request was sent. Do not refresh or exit this page."
                        onCancel={async () => {
                            hidePopover()
                            await RecordFactory.getInstance().cancelInternetReaderIntent(
                                selectedReader
                            )
                            postNotification('IntentStatusChanged', {
                                pendingIntent: undefined,
                                status: 'canceled',
                            })
                        }}
                    />
                )
            }

            hidePopover()
            setFinalCharge(result.value)
            goToNextStep(offset)
        },
        [handlePayment, hidePopover, goToNextStep, selectedReader, postNotification]
    )

    const onNotification = useCallback(
        async (data: { completeIntent?: string; failedIntent?: string } | undefined) => {
            if (!data) return

            if (data.completeIntent && data.completeIntent === intent) {
                payment.intent = data.completeIntent

                showPopover(
                    <NewChargeProcessingPopover
                        title="Confirming"
                        subtitle="Confirming payment. Do not refresh or exit this page."
                    />
                )
                const result = await submitCharge(payment, trandoc)

                if (!result.ok) {
                    return showPopover(
                        <ProcessingFailedPopover
                            message={result.error?.toString() ?? 'Unknown error'}
                            onClose={() => {
                                hidePopover()
                            }}
                        />
                    )
                }
                hidePopover()
                setFinalCharge(result.value)
                setSearchParams({ step: 'send' })
            }

            if (data.failedIntent && data.failedIntent === intent) {
                showPopover(
                    <ProcessingFailedPopover
                        message="Please try another payment method"
                        onClose={() => {
                            hidePopover()
                        }}
                    />
                )
            }
        },
        [payment, trandoc, intent]
    )

    useNotificationListener(
        'IntentStatusChanged',
        onNotification,
        `charge::intent::${trandoc?.id ?? '-'}`
    )

    const footerContent = useMemo(() => {
        switch (currentStep.type) {
            case 'charge':
                if (isNextEnabled) {
                    return (
                        <div className="flex w-full items-center justify-end gap-x-4">
                            <Button
                                intent="primary"
                                onClick={() => goToNextStep()}
                                disabled={!isNextEnabled}
                            >
                                Next
                            </Button>
                        </div>
                    )
                }
                return undefined
            case 'payment':
                if (!me.allowGratuity && !selectedReader) {
                    return (
                        <div className="flex w-full items-center justify-end gap-x-4">
                            <Button
                                intent="primary"
                                onClick={() => handleChargeAndNavigate(1)}
                                disabled={!isNextEnabled}
                            >
                                {payment.source !== undefined ? 'Charge' : 'Mark paid'}
                            </Button>
                        </div>
                    )
                }
                if (selectedReader) {
                    return (
                        <div className="flex w-full items-center justify-end gap-x-4">
                            <Button
                                intent="primary"
                                onClick={() => handleChargeAndNavigate(2)} // offset is 2 because we skip the gratuity step when using a reader
                                disabled={!isNextEnabled}
                            >
                                Charge
                            </Button>
                        </div>
                    )
                }
                return (
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button
                            intent="primary"
                            onClick={() => goToNextStep()}
                            disabled={!isNextEnabled}
                        >
                            Next
                        </Button>
                    </div>
                )
            case 'gratuity':
                return (
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button
                            intent="primary"
                            onClick={() => handleChargeAndNavigate(1)}
                            disabled={!isNextEnabled}
                        >
                            {payment.source !== undefined || selectedReader !== undefined
                                ? 'Charge'
                                : 'Mark paid'}
                        </Button>
                    </div>
                )
            case 'send':
                return undefined
        }
    }, [goToNextStep, handleChargeAndNavigate, currentStep, isNextEnabled, payment, selectedReader])

    return (
        <WorkflowPage
            steps={steps.map((s) => capitalizeFirstLetters(s.type))}
            title={currentStep.title}
            currentStepIndex={
                // on the final send step, make the index one greater than the actual index to force the final progress icon to be checked
                currentStep.type === 'send' ? currentStep.index + 1 : currentStep.index
            }
            shouldForceCurrentStepCheckmark={currentStep.type === 'send'}
            shouldVerticallyCenterContent={currentStep.type === 'send'}
            onClose={() => {
                navigate(viewRecordPathname, { replace: true })
            }}
            backButtonTitle={backButtonTitle}
            onBack={() => {
                if (currentStep.index === 0) {
                    navigate(viewRecordPathname, { replace: true })
                }
                else {
                    goToPreviousStep()
                }
            }}
            footerContent={footerContent}
        >
            <div className="mt-12">
                {currentStep.type === 'charge' && (
                    <NewChargePage
                        payment={payment}
                        taxable={taxable}
                        items={items}
                        categories={categories}
                        onUpdatePayment={setPayment}
                        minimumAmount={minimumAmount}
                        maximumAmount={maximumAmount}
                        usesPaymentPlan={usesPaymentPlan}
                        trueMaximumAmount={trueMaximumAmount}
                    />
                )}
                {currentStep.type === 'payment' && (
                    <NewChargePaymentPage
                        payment={payment}
                        readers={readers}
                        onUpdatePayment={setPayment}
                        selectedReader={selectedReader}
                        onSelectReader={setSelectedReader}
                        canPayViaOther={willFulfillPaymentPlan || canPayViaOther}
                    />
                )}
                {currentStep.type === 'gratuity' && (
                    <NewChargeGratuityPage payment={payment} onUpdatePayment={setPayment} />
                )}
                {currentStep.type === 'send' && finalCharge && (
                    <NewChargeCompletePage
                        finalRecord={finalCharge}
                        originalRecord={getOriginalRecord(finalCharge)}
                        onBackPathName={viewRecordPathname}
                    />
                )}
            </div>
        </WorkflowPage>
    )
}

const render = (props: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    return (
        <WorkflowPage.ResetContainer>
            <ChargeWorkflowRoot {...props} />
        </WorkflowPage.ResetContainer>
    )
}

export const ChargeWorkflow = usePSComponent(render, viewWillAppear)
